/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import FilterListOutlinedIcon from '@mui/icons-material/FilterListOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { CustomFilterV2, EmptyState, TableV2 } from '../../../components/v2';
import * as selectorsAuth from '../../../store/auth/reducer';
import * as selectorsModuleConfiguration from '../../../store/moduleConfiguration/reducer';
import * as actionsNsir from '../../../store/nsir/actions';
import { canViewUsoMutuoReport } from '../../../helpers/usoMutuoReportPermission';
import TableService from '../../../services/TableService';
import BaseService from '../../../services/BaseService';
import { getPeriodoFromOptionId, getPeriodoOptions } from '../Nsir/utils/usoMutuoPeriodo';
import ChartsRow from './ChartsRow';
import KpiStrip from './KpiStrip';
import MalhaSummaryTable from './MalhaSummaryTable';
import PeriodoEmptyHint from './PeriodoEmptyHint';
import PeriodoHistoricoSection from './PeriodoHistoricoSection';
import PeriodoWeekChips from './PeriodoWeekChips';
import PesagemMatrix from './PesagemMatrix';
import ViewTabs from './ViewTabs';
import {
  EMPTY_MATRIX,
  EMPTY_RESUMO,
  VIEW_TAB_MATRIX,
  VIEW_TAB_MALHA,
  applyPeriodoOptionToFilter,
  buildPeriodoFilterOptions,
  formatPeriodoSucataLabel,
  getDefaultFilter,
  getHeaderPeriodoChips,
  getKpiLabel,
  getPeriodoHeaderPrefix,
  isResumoEmpty,
  normalizeFilterForApi,
  normalizeMatrix,
  normalizeResumo,
  resolveKpiStatusFilter,
  resolveSuggestedPeriodoFromHistorico,
} from './constants';
import {
  detailGridSx,
  getUsoMutuoRowId,
  renderNsirCol,
  renderPeriodoCol,
  renderPesoCol,
  renderStatusCol,
} from './tableCells';

const PERIODO_OPTIONS = getPeriodoOptions();
const HEADER_PERIODO_CHIPS = getHeaderPeriodoChips(PERIODO_OPTIONS);

const RelatorioUsoMutuoV2 = ({ authData, moduleConfigurations, generateReportPesagemSucata }) => {
  const canView = canViewUsoMutuoReport({ authData, moduleConfigurations });

  const defaultFilter = useMemo(() => getDefaultFilter(), []);

  const [localFilter, setLocalFilter] = useState(defaultFilter);
  const [activeStatus, setActiveStatus] = useState(null);
  const [activeMatrixRow, setActiveMatrixRow] = useState(null);
  const [activeMalhaDrill, setActiveMalhaDrill] = useState(null);
  const [activeViewTab, setActiveViewTab] = useState(VIEW_TAB_MATRIX);
  const [openFilter, setOpenFilter] = useState(false);
  const [reload, setReload] = useState(false);
  const [resumo, setResumo] = useState(EMPTY_RESUMO);
  const [resumoLoading, setResumoLoading] = useState(false);
  const [matrix, setMatrix] = useState(EMPTY_MATRIX);
  const [matrixLoading, setMatrixLoading] = useState(false);
  const [malhaSummary, setMalhaSummary] = useState([]);
  const [malhaSummaryLoading, setMalhaSummaryLoading] = useState(false);
  const [historicoItems, setHistoricoItems] = useState([]);

  const apiFilterBase = useMemo(
    () => normalizeFilterForApi(localFilter),
    [localFilter],
  );

  const apiFilter = useMemo(() => {
    let filter = { ...localFilter };

    const statusFilter = resolveKpiStatusFilter(activeStatus);
    if (statusFilter?.length) {
      filter = { ...filter, status: statusFilter };
    }

    if (activeMatrixRow?.nsirId) {
      filter = { ...filter, nsirId: activeMatrixRow.nsirId };
    }

    if (activeMatrixRow?.malhaId) {
      filter = { ...filter, malhaIdDrill: activeMatrixRow.malhaId };
    }

    if (activeMalhaDrill?.malhaId) {
      filter = { ...filter, malhaIdDrill: activeMalhaDrill.malhaId };
    }

    if (activeMalhaDrill?.materialTipo) {
      filter = { ...filter, materialTipo: activeMalhaDrill.materialTipo };
    }

    return normalizeFilterForApi(filter);
  }, [localFilter, activeStatus, activeMatrixRow, activeMalhaDrill]);

  const periodoLabel = useMemo(
    () => formatPeriodoSucataLabel(localFilter.periodoInicio, localFilter.periodoFim),
    [localFilter.periodoInicio, localFilter.periodoFim],
  );

  const periodoPrefix = useMemo(
    () => getPeriodoHeaderPrefix(localFilter.periodoOptionId, PERIODO_OPTIONS),
    [localFilter.periodoOptionId],
  );

  const suggestedFromHistorico = useMemo(
    () => resolveSuggestedPeriodoFromHistorico(historicoItems, PERIODO_OPTIONS),
    [historicoItems],
  );

  const suggestedPeriodo = useMemo(() => {
    if (suggestedFromHistorico) return suggestedFromHistorico;
    const fallback = PERIODO_OPTIONS[1];
    if (!fallback) return null;
    return {
      periodoOptionId: fallback.id,
      periodoInicio: fallback.periodoInicio,
      periodoFim: fallback.periodoFim,
      label: formatPeriodoSucataLabel(fallback.periodoInicio, fallback.periodoFim),
    };
  }, [suggestedFromHistorico]);

  const showEmptyHint = !resumoLoading
    && isResumoEmpty(resumo)
    && !!suggestedPeriodo
    && suggestedPeriodo.periodoOptionId !== localFilter.periodoOptionId;

  const emptyCtaLabel = suggestedFromHistorico
    ? 'Ver última semana com dados'
    : 'Ver semana passada';

  const matrixRowKey = activeMatrixRow
    ? `${activeMatrixRow.nsirId || ''}-${activeMatrixRow.municipio || ''}`
    : null;

  const clearDrills = useCallback(() => {
    setActiveStatus(null);
    setActiveMatrixRow(null);
    setActiveMalhaDrill(null);
  }, []);

  const applyPeriodoOptionId = useCallback((periodoOptionId) => {
    if (!periodoOptionId) return;
    setLocalFilter((prev) => ({
      ...applyPeriodoOptionToFilter(prev, periodoOptionId, PERIODO_OPTIONS),
      newFlow: true,
    }));
    clearDrills();
    setReload((prev) => !prev);
  }, [clearDrills]);

  const fetchResumo = useCallback(async () => {
    if (!canView) return;
    setResumoLoading(true);
    try {
      const response = await TableService.request(
        'reports/uso-mutuo/resumo',
        apiFilterBase,
      );
      setResumo(normalizeResumo(response?.data));
    } catch {
      setResumo(EMPTY_RESUMO);
    } finally {
      setResumoLoading(false);
    }
  }, [apiFilterBase, reload, canView]);

  const fetchMatrix = useCallback(async () => {
    if (!canView) return;
    setMatrixLoading(true);
    try {
      const response = await TableService.request(
        'reports/uso-mutuo/matrix',
        apiFilterBase,
      );
      setMatrix(normalizeMatrix(response?.data));
    } catch {
      setMatrix(EMPTY_MATRIX);
    } finally {
      setMatrixLoading(false);
    }
  }, [apiFilterBase, reload, canView]);

  const fetchMalhaSummary = useCallback(async () => {
    if (!canView) return;
    setMalhaSummaryLoading(true);
    try {
      const response = await BaseService.post({
        url: 'reports/uso-mutuo/summary-by-malha',
        data: apiFilterBase,
      });
      setMalhaSummary(response?.data?.items || []);
    } catch {
      setMalhaSummary([]);
    } finally {
      setMalhaSummaryLoading(false);
    }
  }, [apiFilterBase, reload, canView]);

  useEffect(() => {
    fetchResumo();
  }, [fetchResumo]);

  useEffect(() => {
    fetchMatrix();
  }, [fetchMatrix]);

  useEffect(() => {
    fetchMalhaSummary();
  }, [fetchMalhaSummary]);

  const handleSelectStatus = useCallback((statusKey) => {
    setActiveStatus(statusKey);
    if (statusKey) {
      setActiveMalhaDrill(null);
    }
  }, []);

  const handleMatrixRowSelect = useCallback((row) => {
    setActiveMatrixRow(row);
    if (row) {
      setActiveMalhaDrill(null);
      setActiveStatus(null);
    }
  }, []);

  const handleMalhaSummarySelect = useCallback((item) => {
    if (!item) {
      setActiveMalhaDrill(null);
      return;
    }
    setActiveMalhaDrill({ malhaId: item.malhaId, malhaCodigo: item.malhaCodigo });
    setActiveMatrixRow(null);
    setActiveStatus(null);
  }, []);

  const handleMalhaSegmentSelect = useCallback((segment) => {
    if (!segment) {
      setActiveMalhaDrill(null);
      return;
    }
    setActiveMalhaDrill(segment);
    setActiveMatrixRow(null);
    setActiveStatus(null);
  }, []);

  const clearMatrixDrill = useCallback(() => setActiveMatrixRow(null), []);
  const clearMalhaDrill = useCallback(() => setActiveMalhaDrill(null), []);

  const handleHistoricoItemsLoaded = useCallback((items) => {
    setHistoricoItems(Array.isArray(items) ? items : []);
  }, []);

  const handleApplySuggestedPeriodo = useCallback(() => {
    if (!suggestedPeriodo?.periodoOptionId) return;
    applyPeriodoOptionId(suggestedPeriodo.periodoOptionId);
  }, [suggestedPeriodo, applyPeriodoOptionId]);

  const onExport = () => {
    generateReportPesagemSucata(normalizeFilterForApi(localFilter), () => {});
  };

  const columns = useMemo(
    () => [
      {
        field: 'nsirCol',
        headerName: 'Lote / Região',
        flex: 1.2,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => renderNsirCol(params.row),
      },
      {
        field: 'inspetor',
        headerName: 'Inspetor',
        flex: 1,
        minWidth: 120,
        sortable: false,
      },
      {
        field: 'statusCol',
        headerName: 'Status',
        flex: 1,
        minWidth: 150,
        sortable: false,
        renderCell: (params) => renderStatusCol(params.row),
      },
      {
        field: 'periodoCol',
        headerName: 'Período',
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: (params) => renderPeriodoCol(params.row),
      },
      {
        field: 'pesoCol',
        headerName: 'Pesagem',
        flex: 1.1,
        minWidth: 200,
        sortable: false,
        renderCell: (params) => renderPesoCol(params.row),
      },
    ],
    [],
  );

  const periodoSelectOptions = useMemo(() => buildPeriodoFilterOptions(), []);

  if (!canView) {
    return (
      <Box>
        <EmptyState
          title="Acesso restrito"
          message="O Relatório de Pesagem Sucata está disponível apenas para usuários com permissão RELATORIO_USO_MUTUO."
        />
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={3}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ pr: 2 }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="h4">Relatório Uso Mutuo - Sucata</Typography>
            {periodoLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {periodoPrefix}: {periodoLabel}
              </Typography>
            )}
            <Box sx={{ mt: 1.5 }}>
              <PeriodoWeekChips
                chips={HEADER_PERIODO_CHIPS}
                value={localFilter.periodoOptionId}
                onChange={applyPeriodoOptionId}
              />
            </Box>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<FilterListOutlinedIcon />}
              onClick={() => setOpenFilter(true)}
            >
              Filtros
            </Button>
            <Button
              variant="contained"
              startIcon={<DescriptionOutlinedIcon />}
              onClick={onExport}
            >
              Exportar
            </Button>
          </Stack>
        </Stack>

        {showEmptyHint && suggestedPeriodo && (
          <PeriodoEmptyHint
            ctaLabel={emptyCtaLabel}
            suggestedLabel={suggestedPeriodo.label}
            onApplySuggested={handleApplySuggestedPeriodo}
          />
        )}

        <KpiStrip
          resumo={resumo}
          loading={resumoLoading}
          activeStatus={activeStatus}
          onSelectStatus={handleSelectStatus}
        />

        <PeriodoHistoricoSection
          localFilter={localFilter}
          refreshKey={reload}
          onItemsLoaded={handleHistoricoItemsLoaded}
        />

        <Box>
          <ViewTabs value={activeViewTab} onChange={setActiveViewTab} />
          <Box sx={{ pt: 2 }}>
            {activeViewTab === VIEW_TAB_MATRIX ? (
              <PesagemMatrix
                matrix={matrix}
                loading={matrixLoading}
                selectedRowKey={matrixRowKey}
                onRowSelect={handleMatrixRowSelect}
              />
            ) : (
              <MalhaSummaryTable
                items={malhaSummary}
                loading={malhaSummaryLoading}
                selectedMalhaId={activeMalhaDrill?.malhaId}
                onMalhaSelect={handleMalhaSummarySelect}
              />
            )}
          </Box>
        </Box>

        <ChartsRow
          apiFilterBase={apiFilterBase}
          refreshKey={reload}
          onMalhaSegmentSelect={handleMalhaSegmentSelect}
        />

        <Box>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 2, pr: 2 }}
            flexWrap="wrap"
            useFlexGap
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <InfoOutlinedIcon fontSize="small" color="action" />
              <Typography variant="h6">Detalhamento</Typography>
            </Stack>
            {activeStatus && (
              <Chip
                size="small"
                label={`Status: ${getKpiLabel(activeStatus)}`}
                onDelete={() => setActiveStatus(null)}
                sx={{ fontWeight: 500 }}
              />
            )}
            {activeMatrixRow && (
              <Chip
                size="small"
                label={`Lote: ${activeMatrixRow.municipio || activeMatrixRow.nsirCodigo}`}
                onDelete={clearMatrixDrill}
                sx={{ fontWeight: 500 }}
              />
            )}
            {activeMalhaDrill?.malhaCodigo && (
              <Chip
                size="small"
                label={`Malha: ${activeMalhaDrill.malhaCodigo}${
                  activeMalhaDrill.materialLabel ? ` · ${activeMalhaDrill.materialLabel}` : ''
                }`}
                onDelete={clearMalhaDrill}
                sx={{ fontWeight: 500 }}
              />
            )}
          </Stack>
          <TableV2
            useApi
            fetchUrl="reports/uso-mutuo/filter"
            paginationUrl="/v2/relatorio-uso-mutuo"
            filterData={apiFilter}
            refreshKey={reload}
            rowHeight={112}
            gridSx={detailGridSx}
            getRowId={getUsoMutuoRowId}
            pageSize={10}
            columns={columns}
            emptyMessage="Nenhuma pesagem encontrada"
          />
        </Box>

        <CustomFilterV2
          variant="dialog"
          open={openFilter}
          onClose={() => setOpenFilter(false)}
          title="Filtros"
          fullWidth
          defaultFilter={defaultFilter}
          appliedFilter={localFilter}
          confirmButtonText="Aplicar"
          fields={[
            {
              field: 'useSelectOption',
              key: 'periodoOptionId',
              label: 'Período sucata',
              options: periodoSelectOptions,
              md: 12,
              xs: 12,
            },
            {
              field: 'useCodigo',
              codigoLabel: 'Lote / Município',
              md: 6,
              xs: 12,
            },
            {
              field: 'useInspetor',
              multiSelect: true,
              md: 6,
              xs: 12,
            },
            { field: 'useMalha', md: 6, xs: 12, multiSelect: true },
            { field: 'useGerencia', md: 6, xs: 12 },
          ]}
          onFilter={(filterData) => {
            let next = { ...filterData };
            if (next.periodoOptionId) {
              const periodo = getPeriodoFromOptionId(next.periodoOptionId, PERIODO_OPTIONS);
              if (periodo) {
                next = {
                  ...next,
                  periodoInicio: periodo.periodoInicio,
                  periodoFim: periodo.periodoFim,
                };
              }
            } else {
              next = applyPeriodoOptionToFilter(next, next.periodoOptionId, PERIODO_OPTIONS);
            }
            setLocalFilter({ ...next, newFlow: true });
            clearDrills();
            setOpenFilter(false);
            setReload((prev) => !prev);
          }}
        />
      </Stack>
    </Box>
  );
};

RelatorioUsoMutuoV2.propTypes = {
  authData: PropTypes.object.isRequired,
  moduleConfigurations: PropTypes.array.isRequired,
  generateReportPesagemSucata: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  authData: selectorsAuth.getAuth(state),
  moduleConfigurations: selectorsModuleConfiguration.getModuleConfigurations(state),
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      generateReportPesagemSucata: actionsNsir.generateReportPesagemSucata,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(RelatorioUsoMutuoV2);
